import type { ModelGetSalePlan, ModelSalePlan } from '@app/api/SalePlans';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import {
  GENERAL_PLAN_LABEL,
  SP_APRIL,
  SP_AUGUST,
  SP_CATEGORY,
  SP_DECEMBER,
  SP_FEBRUARY,
  SP_JANUARY,
  SP_JULY,
  SP_JUNE,
  SP_MARCH,
  SP_MAY,
  SP_NOVEMBER,
  SP_OCTOBER,
  SP_SEPTEMBER,
  SP_TABLE_COLUMNS,
  SP_TOTAL,
  type SalePlanMonthFrontKey,
  type SalePlanTableColumn,
} from '@pages/SalePlans/constants/spTableColumns';

export const generateSalePlanRows = (serverData: ModelGetSalePlan | null) => {
  const rows = [];
  let sortedSalePlans: ModelSalePlan[] = [];

  //   ШАГ 1. Проверить, что данные для обработки есть
  if (serverData !== null) {
    // ШАГ 2. Создать вторую строку таблицы (итог по месяцам)
    rows.push({
      rowKey: 'general-plan',
      [SP_CATEGORY]: generateSpanWithDataTestId(
        GENERAL_PLAN_LABEL,
        'overall-plan'
      ),
      [SP_JANUARY]: addMonthPlanData(serverData, SP_JANUARY) || 0,
      [SP_FEBRUARY]: addMonthPlanData(serverData, SP_FEBRUARY) || 0,
      [SP_MARCH]: addMonthPlanData(serverData, SP_MARCH) || 0,
      [SP_APRIL]: addMonthPlanData(serverData, SP_APRIL) || 0,
      [SP_MAY]: addMonthPlanData(serverData, SP_MAY) || 0,
      [SP_JUNE]: addMonthPlanData(serverData, SP_JUNE) || 0,
      [SP_JULY]: addMonthPlanData(serverData, SP_JULY) || 0,
      [SP_AUGUST]: addMonthPlanData(serverData, SP_AUGUST) || 0,
      [SP_SEPTEMBER]: addMonthPlanData(serverData, SP_SEPTEMBER) || 0,
      [SP_OCTOBER]: addMonthPlanData(serverData, SP_OCTOBER) || 0,
      [SP_NOVEMBER]: addMonthPlanData(serverData, SP_NOVEMBER) || 0,
      [SP_DECEMBER]: addMonthPlanData(serverData, SP_DECEMBER) || 0,
      [SP_TOTAL]: calculateTotalByGeneralPlan(serverData),
    });

    // ШАГ 3. Отсортировать данные.
    // Сортируем копию: serverData — это кэш RTK Query, замороженный immer,
    // и sort по месту падает с «Cannot assign to read only property».
    sortedSalePlans = [...(serverData.SalePlans || [])].sort((a, b) => {
      const nameA = a.ClientServiceCategory?.toLowerCase() || '';
      const nameB = b.ClientServiceCategory?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    // ШАГ 4. Генерация основных строк таблицы
    extractCategoryIds(sortedSalePlans).forEach(
      (categoryId: ModelSalePlan['ClientServiceCategoryId']) => {
        const handledCategoryName =
          (sortedSalePlans || []).find(
            ({ ClientServiceCategoryId: _categoryId }) =>
              _categoryId === categoryId
          )?.ClientServiceCategory || 'Без названия';

        return rows.push({
          rowKey: `category-${categoryId}`,
          categoryId,
          planIds: extractCellIdsFromRow(sortedSalePlans, categoryId),
          [SP_CATEGORY]: generateSpanWithDataTestId(
            handledCategoryName,
            'category-name'
          ),
          [SP_JANUARY]: extractPlanMoney(
            sortedSalePlans,
            categoryId,
            SP_JANUARY
          ),
          [SP_FEBRUARY]: extractPlanMoney(
            sortedSalePlans,
            categoryId,
            SP_FEBRUARY
          ),
          [SP_MARCH]: extractPlanMoney(sortedSalePlans, categoryId, SP_MARCH),
          [SP_APRIL]: extractPlanMoney(sortedSalePlans, categoryId, SP_APRIL),
          [SP_MAY]: extractPlanMoney(sortedSalePlans, categoryId, SP_MAY),
          [SP_JUNE]: extractPlanMoney(sortedSalePlans, categoryId, SP_JUNE),
          [SP_JULY]: extractPlanMoney(sortedSalePlans, categoryId, SP_JULY),
          [SP_AUGUST]: extractPlanMoney(sortedSalePlans, categoryId, SP_AUGUST),
          [SP_SEPTEMBER]: extractPlanMoney(
            sortedSalePlans,
            categoryId,
            SP_SEPTEMBER
          ),
          [SP_OCTOBER]: extractPlanMoney(
            sortedSalePlans,
            categoryId,
            SP_OCTOBER
          ),
          [SP_NOVEMBER]: extractPlanMoney(
            sortedSalePlans,
            categoryId,
            SP_NOVEMBER
          ),
          [SP_DECEMBER]: extractPlanMoney(
            sortedSalePlans,
            categoryId,
            SP_DECEMBER
          ),
          [SP_TOTAL]:
            serverData.TotalPlanMoneyByServices?.[categoryId].Total || '',
        });
      }
    );

    return rows;
  } else {
    return [];
  }
};

function addMonthPlanData(
  serverData: ModelGetSalePlan,
  monthFrontKey: SalePlanMonthFrontKey
) {
  const monthServerKey: SalePlanTableColumn['serverKey'] =
    SP_TABLE_COLUMNS.find(({ id }) => id === monthFrontKey)?.serverKey;
  const handledMonthServerKey = monthServerKey ? +monthServerKey : undefined;

  if (!handledMonthServerKey) return 'Произошла ошибка';

  return serverData.TotalPlanMoneyByMonths?.[handledMonthServerKey];
}

function extractCategoryIds(sortedSalePlans: ModelSalePlan[]) {
  const duplicatedIds = (Object(sortedSalePlans) || []).map(
    ({ ClientServiceCategoryId }: ModelSalePlan) => ClientServiceCategoryId
  );
  const uniqueIds: Set<ModelSalePlan['ClientServiceCategoryId']> = new Set(
    duplicatedIds
  );

  return uniqueIds;
}

function extractPlanMoney(
  sortedSalePlans: ModelSalePlan[],
  categoryId: ModelSalePlan['ClientServiceCategoryId'],
  monthFrontId: string
) {
  const monthJsIndex = SP_TABLE_COLUMNS.find(({ id }) => {
    if (id === monthFrontId) {
      return true;
    }
  })?.monthJsIndex;

  return (
    sortedSalePlans
      .filter(
        ({ ClientServiceCategoryId }) => ClientServiceCategoryId === categoryId
      )
      .find(({ MonthYear }) => new Date(MonthYear).getMonth() === monthJsIndex)
      ?.PlanMoney || '-'
  );
}

function extractCellIdsFromRow(
  sortedSalePlans: ModelSalePlan[],
  categoryId: ModelSalePlan['ClientServiceCategoryId']
) {
  return sortedSalePlans.filter(
    ({ ClientServiceCategoryId }) => ClientServiceCategoryId === categoryId
  );
}

function calculateTotalByGeneralPlan(serverData: ModelGetSalePlan) {
  const totalByMonths = [];
  let result = 0n;

  if (serverData.TotalPlanMoneyByMonths) {
    for (const val of Object.values(serverData.TotalPlanMoneyByMonths)) {
      totalByMonths.push(val);
    }

    totalByMonths.forEach((item) => (result += BigInt(item)));

    return result;
  } else {
    return '';
  }
}
